import React, { useState, useMemo } from 'react';
import {
  Calendar, Search, ChevronLeft, ChevronRight, Check, X,
  Clock, MapPin, User, Users, Play, AlertCircle, RefreshCw
} from 'lucide-react';
import {
  seances, produits, inscriptions, professeurs, preferences,
  getProduit, getProfesseur, getCoursProduits,
  formatDate, genererSeancesPourCours
} from '../data/mockData';

const Seances = () => {
  const [allSeances, setAllSeances] = useState(seances);
  const [selectedCoursId, setSelectedCoursId] = useState('');
  const [filterStatut, setFilterStatut] = useState('tous');
  const [selectedSeance, setSelectedSeance] = useState(null);
  const [showGenModal, setShowGenModal] = useState(false);
  const [genCoursId, setGenCoursId] = useState('');

  const coursList = getCoursProduits();

  const filtered = useMemo(() => {
    return allSeances.filter(s => {
      const matchCours = !selectedCoursId || s.produitId === selectedCoursId;
      const matchStatut = filterStatut === 'tous' || s.statut === filterStatut;
      return matchCours && matchStatut;
    }).sort((a, b) => a.date.localeCompare(b.date));
  }, [allSeances, selectedCoursId, filterStatut]);

  const totalEffectuees = allSeances.filter(s => s.statut === 'effectuee').length;
  const totalPlanifiees = allSeances.filter(s => s.statut === 'planifiee').length;
  const totalAnnulees = allSeances.filter(s => s.statut === 'annulee').length;

  const getStatusBadge = (statut) => {
    const map = {
      effectuee: <span className="badge badge-success">Effectuée</span>,
      planifiee: <span className="badge badge-primary">Planifiée</span>,
      annulee: <span className="badge badge-danger">Annulée</span>,
    };
    return map[statut] || <span className="badge">{statut}</span>;
  };

  const getElevesForCours = (produitId) => {
    return inscriptions.filter(ins =>
      (ins.statut === 'validee' || ins.statut === 'facturee') &&
      ins.produitsIds.includes(produitId)
    );
  };

  const handleGenerer = () => {
    if (!genCoursId) return;
    const cours = getProduit(genCoursId);
    if (!cours) return;
    const newSeances = genererSeancesPourCours(cours, preferences.debutAnnee, preferences.finAnnee);
    const existingDates = allSeances
      .filter(s => s.produitId === genCoursId)
      .map(s => s.date);
    const uniqueNew = newSeances.filter(s => !existingDates.includes(s.date));
    setAllSeances(prev => [...prev, ...uniqueNew]);
    setShowGenModal(false);
    setSelectedCoursId(genCoursId);
  };

  const togglePresence = (seance, inscriptionId) => {
    setAllSeances(prev => prev.map(s => {
      if (s.id !== seance.id) return s;
      const presentes = s.inscriptionsPresentes.includes(inscriptionId)
        ? s.inscriptionsPresentes.filter(id => id !== inscriptionId)
        : [...s.inscriptionsPresentes, inscriptionId];
      return { ...s, inscriptionsPresentes: presentes };
    }));
    if (selectedSeance && selectedSeance.id === seance.id) {
      setSelectedSeance(prev => {
        const presentes = prev.inscriptionsPresentes.includes(inscriptionId)
          ? prev.inscriptionsPresentes.filter(id => id !== inscriptionId)
          : [...prev.inscriptionsPresentes, inscriptionId];
        return { ...prev, inscriptionsPresentes: presentes };
      });
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1><Calendar size={28} /> Séances</h1>
          <div className="page-meta">
            <span>Année : {preferences.debutAnnee} → {preferences.finAnnee}</span>
            <span>{allSeances.length} séance(s)</span>
          </div>
        </div>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => { setShowGenModal(true); setGenCoursId(coursList[0]?.id || ''); }}>
            <RefreshCw size={16} /> Générer les séances
          </button>
        </div>
      </div>

      <div className="stats-grid" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
        <div className="stat-card stat-success">
          <div className="stat-icon"><Check size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Effectuées</p>
            <p className="stat-value">{totalEffectuees}</p>
          </div>
        </div>
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Clock size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Planifiées</p>
            <p className="stat-value">{totalPlanifiees}</p>
          </div>
        </div>
        <div className="stat-card stat-warning">
          <div className="stat-icon"><AlertCircle size={24} /></div>
          <div className="stat-content">
            <p className="stat-label">Annulées</p>
            <p className="stat-value">{totalAnnulees}</p>
          </div>
        </div>
      </div>

      <div className="filters-bar">
        <select
          className="filter-select"
          value={selectedCoursId}
          onChange={(e) => setSelectedCoursId(e.target.value)}
          style={{ minWidth: '300px' }}
        >
          <option value="">Tous les cours</option>
          {coursList.map(c => (
            <option key={c.id} value={c.id}>{c.nom}</option>
          ))}
        </select>
        <div className="filter-tabs">
          {[
            { id: 'tous', label: 'Toutes' },
            { id: 'effectuee', label: 'Effectuées' },
            { id: 'planifiee', label: 'Planifiées' },
            { id: 'annulee', label: 'Annulées' },
          ].map(f => (
            <button
              key={f.id}
              className={`filter-tab ${filterStatut === f.id ? 'active' : ''}`}
              onClick={() => setFilterStatut(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {selectedCoursId && (() => {
        const cours = getProduit(selectedCoursId);
        const prof = cours ? getProfesseur(cours.professeurId) : null;
        const elevesInscrits = getElevesForCours(selectedCoursId);
        return cours && (
          <div className="cours-info-card">
            <div className="cours-info-header">
              <div className="cours-info-title">
                <h3>{cours.nom}</h3>
                {getStatusBadge('planifiee')}
              </div>
            </div>
            <div className="cours-info-meta">
              {prof && <span><User size={14} /> {prof.prenom} {prof.nom}</span>}
              <span><Clock size={14} /> {cours.duree} min</span>
              {cours.salle && <span><MapPin size={14} /> {cours.salle}</span>}
              <span><Users size={14} /> {elevesInscrits.length} élève(s) inscrit(s)</span>
              <span><Calendar size={14} /> {cours.frequence?.join(', ')}</span>
            </div>
          </div>
        );
      })()}

      <div className="factures-table-container">
        <table className="factures-table">
          <thead>
            <tr>
              <th>Date</th>
              <th>Cours</th>
              <th>Horaire</th>
              <th>Statut</th>
              <th>Présents</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(seance => {
              const cours = getProduit(seance.produitId);
              const prof = cours ? getProfesseur(cours.professeurId) : null;
              const elevesInscrits = getElevesForCours(seance.produitId);
              const nbPresents = seance.inscriptionsPresentes.length;

              return (
                <tr key={seance.id}>
                  <td>
                    <strong>{formatDate(seance.date)}</strong>
                    <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>
                      {new Date(seance.date).toLocaleDateString('fr-FR', { weekday: 'long' })}
                    </div>
                  </td>
                  <td>
                    <div>{cours?.nom || '—'}</div>
                    {prof && <div style={{ fontSize: '0.75rem', color: 'var(--gray-500)' }}>{prof.prenom} {prof.nom}</div>}
                  </td>
                  <td>{seance.heureDebut} — {seance.heureFin}</td>
                  <td>{getStatusBadge(seance.statut)}</td>
                  <td>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <strong>{nbPresents}</strong>
                      <span style={{ color: 'var(--gray-400)' }}>/ {elevesInscrits.length}</span>
                    </div>
                    <div className="progress-bar" style={{ width: '80px', height: '4px', marginTop: '0.25rem' }}>
                      <div
                        className="progress-fill"
                        style={{ width: elevesInscrits.length > 0 ? `${(nbPresents / elevesInscrits.length) * 100}%` : '0%' }}
                      />
                    </div>
                  </td>
                  <td>
                    <button
                      className="btn btn-secondary btn-sm"
                      onClick={() => setSelectedSeance(seance)}
                    >
                      <Users size={14} /> Présences
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filtered.length === 0 && (
        <div className="empty-state">
          <Calendar size={48} />
          <h3>Aucune séance</h3>
          <p>Sélectionnez un cours ou générez des séances</p>
        </div>
      )}

      {/* Séance detail / présences modal */}
      {selectedSeance && (() => {
        const cours = getProduit(selectedSeance.produitId);
        const prof = cours ? getProfesseur(cours.professeurId) : null;
        const elevesInscrits = getElevesForCours(selectedSeance.produitId);

        return (
          <div className="modal-overlay" onClick={() => setSelectedSeance(null)}>
            <div className="modal modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Présences — {cours?.nom || 'Séance'}</h2>
                <button className="modal-close" onClick={() => setSelectedSeance(null)}>
                  <X size={20} />
                </button>
              </div>
              <div className="modal-body">
                <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.5rem', flexWrap: 'wrap' }}>
                  <div className="meta-item"><Calendar size={14} /> {formatDate(selectedSeance.date)}</div>
                  <div className="meta-item"><Clock size={14} /> {selectedSeance.heureDebut} — {selectedSeance.heureFin}</div>
                  {prof && <div className="meta-item"><User size={14} /> {prof.prenom} {prof.nom}</div>}
                  {cours?.salle && <div className="meta-item"><MapPin size={14} /> {cours.salle}</div>}
                  <div>{getStatusBadge(selectedSeance.statut)}</div>
                </div>

                <div className="presence-table-container">
                  <table className="presence-table">
                    <thead>
                      <tr>
                        <th>Élève</th>
                        <th className="presence-col">Présent</th>
                      </tr>
                    </thead>
                    <tbody>
                      {elevesInscrits.length === 0 ? (
                        <tr>
                          <td colSpan="2" className="empty-row">Aucun élève inscrit à ce cours</td>
                        </tr>
                      ) : (
                        elevesInscrits.map(ins => {
                          const isPresent = selectedSeance.inscriptionsPresentes.includes(ins.id);
                          return (
                            <tr key={ins.id} className={!isPresent && selectedSeance.statut === 'effectuee' ? 'absent-row' : ''}>
                              <td>
                                <div className="eleve-cell">
                                  <div className="eleve-avatar-mini" style={{ background: ins.color || '#8B5CF6' }}>
                                    {ins.initials}
                                  </div>
                                  <div>
                                    <strong>{ins.eleve.prenom} {ins.eleve.nom}</strong>
                                  </div>
                                </div>
                              </td>
                              <td className="presence-col">
                                <button
                                  className={`presence-btn present ${isPresent ? 'active' : ''}`}
                                  onClick={() => togglePresence(selectedSeance, ins.id)}
                                >
                                  <Check size={16} />
                                </button>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="presence-summary" style={{ marginTop: '1rem' }}>
                  <div className="summary-item">
                    <span className="text-success" style={{ fontWeight: 600 }}>
                      {selectedSeance.inscriptionsPresentes.length} présent(s)
                    </span>
                  </div>
                  <div className="summary-item">
                    <span className="text-danger" style={{ fontWeight: 600 }}>
                      {elevesInscrits.length - selectedSeance.inscriptionsPresentes.length} absent(s)
                    </span>
                  </div>
                  <div className="summary-item">
                    <span style={{ color: 'var(--gray-500)' }}>
                      sur {elevesInscrits.length} inscrit(s)
                    </span>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSelectedSeance(null)}>Fermer</button>
                <button className="btn btn-primary" onClick={() => setSelectedSeance(null)}>
                  <Check size={16} /> Enregistrer
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Generate séances modal */}
      {showGenModal && (
        <div className="modal-overlay" onClick={() => setShowGenModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Générer les séances</h2>
              <button className="modal-close" onClick={() => setShowGenModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <p style={{ marginBottom: '1rem', color: 'var(--gray-600)' }}>
                Génère automatiquement toutes les séances pour un cours entre le <strong>{formatDate(preferences.debutAnnee)}</strong> et le <strong>{formatDate(preferences.finAnnee)}</strong>, selon sa fréquence.
              </p>
              <div className="form-group">
                <label>Cours</label>
                <select
                  className="filter-select"
                  style={{ width: '100%' }}
                  value={genCoursId}
                  onChange={(e) => setGenCoursId(e.target.value)}
                >
                  {coursList.map(c => {
                    const nbExist = allSeances.filter(s => s.produitId === c.id).length;
                    return (
                      <option key={c.id} value={c.id}>
                        {c.nom} — {c.frequence?.join(', ')} ({nbExist} séances existantes)
                      </option>
                    );
                  })}
                </select>
              </div>
              {genCoursId && (() => {
                const c = getProduit(genCoursId);
                if (!c) return null;
                const preview = genererSeancesPourCours(c, preferences.debutAnnee, preferences.finAnnee);
                const existingDates = allSeances.filter(s => s.produitId === genCoursId).map(s => s.date);
                const newCount = preview.filter(s => !existingDates.includes(s.date)).length;
                return (
                  <div style={{ padding: '1rem', background: 'var(--gray-50)', borderRadius: 'var(--radius)', marginTop: '0.5rem' }}>
                    <p><strong>{preview.length}</strong> séances calculées au total</p>
                    <p><strong>{existingDates.length}</strong> séances déjà existantes</p>
                    <p style={{ color: 'var(--primary)', fontWeight: 600 }}>
                      <strong>{newCount}</strong> nouvelle(s) séance(s) à créer
                    </p>
                  </div>
                );
              })()}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowGenModal(false)}>Annuler</button>
              <button className="btn btn-primary" onClick={handleGenerer}>
                <Play size={16} /> Générer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Seances;
